import apiEndPoints from '../../../routeConfig';
import { ApiService } from '../../../../service/api.service';
import { attempt, resultMessage } from '../../../../service/apiResult';

/**
 * Market reference data for eK Score, and the live coverage check over all reference data.
 *
 *   MSP prices   risk.msp_reference      E1_MSP_GAP and the "sale below floor" distress event,
 *                                        only for farmers in the row's country; newest marketing
 *                                        year wins
 *   Crop names   risk.crop_name_aliases  maps a farmer-entered crop name (Dhan, Paddy, Rice) to one
 *                                        canonical name and its data.gov.in Agmarknet commodity,
 *                                        so MSP, soil-moisture and mandi-price lookups find it
 *   Coverage     computed live           every crop and state in use, and which reference data
 *                                        each one has; updates as DAREs add crops
 *
 * Backed by /api/v1/ekutir/climate (super admin only). The duplicate and range rules below are
 * also enforced server-side (409 / 400); they are kept here so the form can mark the field.
 */

const apiService = new ApiService();

const body = (result) => result?.data?.data ?? result?.data ?? null;

const isConflict = (result) => Number(result?.error?.statusCode) === 409;

const trimmed = (value) => String(value ?? '').trim();

const sameName = (a, b) => trimmed(a).toLowerCase() === trimmed(b).toLowerCase();

export const MSP_UNIT = 'INR_PER_QUINTAL';

export const MSP_SEASONS = [
  { code: '', label: 'Any season' },
  { code: 'KHARIF', label: 'Kharif' },
  { code: 'RABI', label: 'Rabi' },
  { code: 'ZAID', label: 'Zaid' },
];

const MARKETING_YEAR = /^\d{4}-\d{2}$/;

export function mspSeasonLabel(code) {
  return MSP_SEASONS.find((season) => season.code === (code || ''))?.label || code;
}

/* ---------------------------------------------------------------- MSP prices */

export function validateMsp(draft, existing = []) {
  const issues = {};

  if (!draft.countryId) {
    issues.countryId = 'Choose the country this price applies in.';
  }

  const cropName = trimmed(draft.cropName);
  if (!cropName) {
    issues.cropName = 'Enter the crop name.';
  } else if (cropName.length > 100) {
    issues.cropName = 'Keep the crop name under 100 characters.';
  }

  if (draft.seasonCode && !MSP_SEASONS.some((season) => season.code === draft.seasonCode)) {
    issues.seasonCode = 'Choose a season.';
  }

  const year = trimmed(draft.marketingYear);
  if (year && !MARKETING_YEAR.test(year)) {
    issues.marketingYear = 'Use the marketing year format 2026-27.';
  } else if (year && (Number(year.slice(0, 4)) + 1) % 100 !== Number(year.slice(5, 7))) {
    issues.marketingYear = 'The second year should follow the first, e.g. 2026-27.';
  }

  const value = Number(draft.mspValue);
  if (!(value > 0)) {
    issues.mspValue = 'Enter the price per quintal, above 0.';
  } else if (Math.abs(Math.round(value * 100) - value * 100) > 1e-6) {
    issues.mspValue = 'Use at most 2 decimals.';
  } else if (value >= 1000000) {
    // msp_reference.msp_value is numeric(10,2); the costliest MSP crop is under ₹15,000/quintal.
    issues.mspValue = 'That is far above any MSP per quintal — check the value.';
  }

  const clash = existing.find(
    (row) =>
      row.id !== draft.id &&
      row.countryId === draft.countryId &&
      sameName(row.cropName, cropName) &&
      (row.seasonCode || '') === (draft.seasonCode || '') &&
      trimmed(row.marketingYear) === year
  );
  if (clash) {
    issues.cropName = 'This crop already has a price for that country, season and year.';
  }

  return issues;
}

export async function getMspPrices() {
  const result = await attempt(() => apiService.get(apiEndPoints.mspPrices));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not load MSP prices.') } };
  }
  return { success: true, data: body(result) || [] };
}

export async function saveMspPrice(draft) {
  const issues = validateMsp(draft);
  if (Object.keys(issues).length) return { success: false, issues, error: { message: 'Fix the highlighted fields.' } };

  const payload = {
    countryId: draft.countryId,
    cropId: draft.cropId || null,
    cropName: trimmed(draft.cropName),
    seasonCode: draft.seasonCode || null,
    marketingYear: trimmed(draft.marketingYear) || null,
    mspValue: Number(draft.mspValue),
    unit: draft.unit || MSP_UNIT,
    notifiedAt: draft.notifiedAt || null,
    notes: trimmed(draft.notes) || null,
  };

  const result = draft.id
    ? await attempt(() => apiService.update(`${apiEndPoints.mspPrices}/${draft.id}`, payload))
    : await attempt(() => apiService.post(apiEndPoints.mspPrices, payload));

  if (!result.success) {
    if (isConflict(result)) {
      return {
        success: false,
        issues: { cropName: 'This crop already has a price for that country, season and year.' },
        error: { message: 'Fix the highlighted fields.' },
      };
    }
    return { success: false, error: { message: resultMessage(result, 'Could not save the MSP price.') } };
  }
  return { success: true, data: body(result) };
}

export async function deleteMspPrice(id) {
  const result = await attempt(() => apiService.delete(`${apiEndPoints.mspPrices}/${id}`));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not delete the MSP price.') } };
  }
  return { success: true, data: null };
}

/** Rows newest marketing year first within each crop, the order the engine picks them in. */
export function sortMspPrices(rows = []) {
  return [...rows].sort(
    (a, b) =>
      trimmed(a.countryName).localeCompare(trimmed(b.countryName)) ||
      trimmed(a.cropName).localeCompare(trimmed(b.cropName)) ||
      trimmed(b.marketingYear).localeCompare(trimmed(a.marketingYear))
  );
}

/* ---------------------------------------------------------------- crop names */

export function validateCropAlias(draft, existing = []) {
  const issues = {};

  const name = trimmed(draft.name);
  if (!name) {
    issues.name = 'Enter the name farmers or DAREs use.';
  } else if (name.length > 100) {
    issues.name = 'Keep the name under 100 characters.';
  }

  const canonicalName = trimmed(draft.canonicalName);
  if (!canonicalName) {
    issues.canonicalName = 'Enter the crop it means, as written in MSP prices and thresholds.';
  } else if (canonicalName.length > 100) {
    issues.canonicalName = 'Keep the crop name under 100 characters.';
  }

  if (trimmed(draft.agmarknetCommodity).length > 150) {
    issues.agmarknetCommodity = 'Keep the commodity under 150 characters.';
  }

  const clash = existing.find((row) => row.id !== draft.id && sameName(row.name, name));
  if (name && clash) {
    issues.name = 'This name is already mapped.';
  }

  return issues;
}

export async function getCropAliases() {
  const result = await attempt(() => apiService.get(apiEndPoints.cropAliases));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not load crop names.') } };
  }
  return { success: true, data: body(result) || [] };
}

export async function saveCropAlias(draft) {
  const issues = validateCropAlias(draft);
  if (Object.keys(issues).length) return { success: false, issues, error: { message: 'Fix the highlighted fields.' } };

  const payload = {
    name: trimmed(draft.name),
    canonicalName: trimmed(draft.canonicalName),
    agmarknetCommodity: trimmed(draft.agmarknetCommodity) || null,
    notes: trimmed(draft.notes) || null,
  };

  const result = draft.id
    ? await attempt(() => apiService.update(`${apiEndPoints.cropAliases}/${draft.id}`, payload))
    : await attempt(() => apiService.post(apiEndPoints.cropAliases, payload));

  if (!result.success) {
    if (isConflict(result)) {
      return { success: false, issues: { name: 'This name is already mapped.' }, error: { message: 'Fix the highlighted fields.' } };
    }
    return { success: false, error: { message: resultMessage(result, 'Could not save the crop name.') } };
  }
  return { success: true, data: body(result) };
}

export async function deleteCropAlias(id) {
  const result = await attempt(() => apiService.delete(`${apiEndPoints.cropAliases}/${id}`));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not delete the crop name.') } };
  }
  return { success: true, data: null };
}

/** Grouped by the crop they mean, then by name. */
export function sortCropAliases(rows = []) {
  return [...rows].sort(
    (a, b) =>
      trimmed(a.canonicalName).localeCompare(trimmed(b.canonicalName)) || trimmed(a.name).localeCompare(trimmed(b.name))
  );
}

/* ---------------------------------------------------------------- coverage */

export async function getReferenceCoverage() {
  const result = await attempt(() => apiService.get(apiEndPoints.referenceCoverage));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not load reference coverage.') } };
  }
  return { success: true, data: normaliseCoverage(body(result)) };
}

/** Defensive shape: the screen renders whatever arrived, never crashes on a missing list. */
export function normaliseCoverage(raw) {
  const crops = Array.isArray(raw?.crops) ? raw.crops : [];
  const regions = Array.isArray(raw?.regions) ? raw.regions : [];
  const gaps = raw?.gaps || {};
  return {
    generatedAt: raw?.generatedAt || null,
    crops: [...crops].sort(
      (a, b) => Number(b.farmers || 0) - Number(a.farmers || 0) || trimmed(a.cropName).localeCompare(trimmed(b.cropName))
    ),
    regions: [...regions].sort(
      (a, b) => Number(b.farmers || 0) - Number(a.farmers || 0) || trimmed(a.stateName).localeCompare(trimmed(b.stateName))
    ),
    gaps: {
      cropsWithoutAlias: Number(gaps.cropsWithoutAlias || 0),
      cropsWithoutMsp: Number(gaps.cropsWithoutMsp || 0),
      cropsWithoutThreshold: Number(gaps.cropsWithoutThreshold || 0),
      farmersAffected: Number(gaps.farmersAffected || 0),
    },
  };
}

/**
 * The status chip for one coverage cell: `ok`, `missing` or `na` (not applicable — MSP outside a
 * country with MSP data).
 */
export function mspStatus(crop) {
  if (!crop?.msp) return { tone: 'na', label: 'n.a.' };
  if (!crop.msp.available) return { tone: 'missing', label: 'Missing' };
  const value = Number(crop.msp.value);
  const amount = Number.isFinite(value) ? `₹${value.toLocaleString('en-IN')}` : '';
  return { tone: 'ok', label: [crop.msp.marketingYear, amount].filter(Boolean).join(' · ') || 'Set' };
}

export function aliasStatus(crop) {
  return crop?.aliasKnown ? { tone: 'ok', label: 'Name mapped' } : { tone: 'missing', label: 'No mapping' };
}

export function thresholdStatus(crop) {
  const value = crop?.soilMoistureThreshold;
  if (value === null || value === undefined || value === '') return { tone: 'missing', label: 'Missing' };
  return { tone: 'ok', label: Number(value).toFixed(2) };
}

export function priceStatus(crop) {
  return crop?.priceSource === 'AGMARKNET'
    ? { tone: 'ok', label: crop.agmarknetCommodity ? `Mandi · ${crop.agmarknetCommodity}` : 'Mandi prices' }
    : { tone: 'na', label: 'No mandi source' };
}

export function rainfallStatus(region) {
  return region?.rainfallNormal === 'OVERRIDE'
    ? { tone: 'info', label: 'Override' }
    : { tone: 'ok', label: 'Automatic (NASA)' };
}

/** Prefill for the "add" quick actions on a coverage row. */
export function mspDraftFor(crop) {
  return {
    countryId: crop?.countryId || '',
    cropId: crop?.cropId || null,
    cropName: crop?.canonicalName || crop?.cropName || '',
  };
}

export function aliasDraftFor(crop) {
  return { name: crop?.cropName || '', canonicalName: crop?.canonicalName || crop?.cropName || '' };
}

export function thresholdDraftFor(crop) {
  return { cropId: crop?.cropId || null, cropName: crop?.canonicalName || crop?.cropName || '' };
}
